/**
 * Create-from-lists confirm handler – create drafts only (no send).
 * Reuses Scale modal; creates prepared drops for each selected list.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';

import {
  filterSenderNamesMax11,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { applyScheduleInterval } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import { scheduleLocalToUtcIso } from '@/features/smswiser-campaigns/utils/scheduleTimezone';
import type { ShortLinkTld } from '@/hooks';
import type { CreatePreparedDropPayload } from '@/hooks/smswiser-campaign-workspace/mutations';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';

function resolveLeadsListIdFromRow(
  row: DropsWithStatsRow,
  leadsLists: Array<{ id: number; name: string }>,
): number | null {
  const name = (row.drop.leadsList ?? '').trim().toLowerCase();
  if (name) {
    const byName = leadsLists.find((l) => (l.name ?? '').trim().toLowerCase() === name);
    if (byName) return byName.id;
  }
  const listIdMatch = /^list-(.+)$/.exec(row.drop.uniqueId ?? '');
  if (listIdMatch) {
    const listIdNum = parseInt(listIdMatch[1], 10);
    if (Number.isFinite(listIdNum)) {
      const byId = leadsLists.find((l) => l.id === listIdNum);
      if (byId) return byId.id;
    }
  }
  return null;
}

export interface CreateFromListsPayload {
  workDate: string;
  routeId: number;
  leadsListWiserId: number;
  shortLinkTldValue: string;
  amount: number;
  executeAfter?: string;
  senderNames: string[];
  smsTexts: string[];
  forceSend?: boolean;
  batchAmount?: number;
  batchDelay?: number;
  useOnlyNewDomains?: boolean;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
  staticTagId?: string | null;
  scheduleIntervalMinutes?: number;
  routeIdOverride?: number | null;
  shortLinkTldOverride?: string | null;
  senderNamesOverride?: string[] | null;
  smsTextsOverride?: string[] | null;
  senderNamesOverridePerDrop?: string[][];
  smsTextsOverridePerDrop?: string[][];
}

export interface CreateFromListsDeps {
  countryKey: string;
  wiserId: number;
  leadsLists: Array<{ id: number; name: string }>;
  routes: Array<{ id: number; name: string }>;
  tlds: ShortLinkTld[];
  creativeSmsTexts: string[] | undefined;
  updateCreative: { mutateAsync: (data: { smsTexts: string[] }) => Promise<unknown> };
  createPreparedFlex: {
    mutateAsync: (opts: {
      countryKey: string;
      payload: CreatePreparedDropPayload;
      wiserId: number;
    }) => Promise<{ id: string }>;
  };
  toastError: (title: string, msg: string) => void;
}

export async function executeCreateFromListsConfirm(
  payload: CreateFromListsPayload,
  rows: DropsWithStatsRow[],
  deps: CreateFromListsDeps,
): Promise<void> {
  if (rows.length === 0) {
    deps.toastError('Copy as draft failed', 'No list selected.');
    throw new Error('No list selected');
  }
  if (deps.wiserId <= 0) {
    deps.toastError('Copy as draft failed', 'Select a campaign.');
    throw new Error('No campaign selected');
  }

  const isSingleRow = rows.length === 1;
  const allTextsFromRows = rows.flatMap((r) =>
    Array.isArray(r.drop.smsText)
      ? r.drop.smsText.filter((t): t is string => typeof t === 'string')
      : r.drop.smsText ? [String(r.drop.smsText)] : [],
  );
  const perDropTexts = payload.smsTextsOverridePerDrop;
  const perDropSenders = payload.senderNamesOverridePerDrop;
  const textsToMerge = isSingleRow
    ? payload.smsTexts
    : perDropTexts != null && perDropTexts.length > 0
      ? perDropTexts.flat()
      : payload.smsTextsOverride != null && payload.smsTextsOverride.length > 0
        ? payload.smsTextsOverride
        : allTextsFromRows;
  const mergedForDb = uniqueKeepOrderStrings([...(deps.creativeSmsTexts ?? []), ...textsToMerge])
    .filter((t) => String(t).trim().length > 0)
    .slice(0, 50);
  await deps.updateCreative.mutateAsync({ smsTexts: mergedForDb });

  const intervalMin = payload.scheduleIntervalMinutes ?? 0;
  const baseTime =
    intervalMin > 0 && rows.length > 1
      ? scheduleLocalToUtcIso(`${payload.workDate}T00:00`)
      : payload.executeAfter ?? new Date().toISOString();

  for (let idx = 0; idx < rows.length; idx++) {
    const row = rows[idx];
    const leadsListWiserId = isSingleRow
      ? payload.leadsListWiserId
      : resolveLeadsListIdFromRow(row, deps.leadsLists);
    if (leadsListWiserId == null || leadsListWiserId <= 0) {
      deps.toastError('Copy as draft failed', `List "${row.drop.title}" not found in leads lists.`);
      throw new Error(`Unknown list: ${row.drop.title}`);
    }
    const routeId =
      payload.routeIdOverride != null && payload.routeIdOverride > 0
        ? payload.routeIdOverride
        : payload.routeId;
    if (routeId == null || routeId <= 0) {
      deps.toastError('Copy as draft failed', 'Select a gateway.');
      throw new Error('No gateway selected');
    }
    const shortLinkTldValue =
      payload.shortLinkTldOverride != null && payload.shortLinkTldOverride.trim() !== ''
        ? payload.shortLinkTldOverride.trim()
        : payload.shortLinkTldValue;
    const senderNames =
      perDropSenders != null && idx < perDropSenders.length && perDropSenders[idx]?.length > 0
        ? filterSenderNamesMax11(perDropSenders[idx].slice(0, 50))
        : payload.senderNamesOverride != null && payload.senderNamesOverride.length > 0
          ? filterSenderNamesMax11(payload.senderNamesOverride.slice(0, 50))
          : filterSenderNamesMax11(payload.senderNames);
    const smsTexts =
      perDropTexts != null && idx < perDropTexts.length && perDropTexts[idx]?.length > 0
        ? perDropTexts[idx].slice(0, 50)
        : payload.smsTextsOverride != null && payload.smsTextsOverride.length > 0
          ? payload.smsTextsOverride.slice(0, 50)
          : payload.smsTexts;
    if (senderNames.length === 0 || smsTexts.length === 0) {
      deps.toastError('Copy as draft failed', 'Select at least one sender and one SMS text.');
      throw new Error('Missing sender/text');
    }
    const dropExecuteAfter =
      intervalMin > 0 && idx > 0 ? applyScheduleInterval(baseTime, intervalMin, idx) : payload.executeAfter;
    const createPayload: CreatePreparedDropPayload = {
      workDate: payload.workDate,
      routeId,
      leadsListWiserId,
      shortLinkTldValue,
      amount: payload.amount,
      executeAfter: dropExecuteAfter,
      senderNames,
      smsTexts,
      forceSend: payload.forceSend,
      batchAmount: payload.batchAmount,
      batchDelay: payload.batchDelay,
      useOnlyNewDomains: payload.useOnlyNewDomains,
      clickIdStructure: payload.clickIdStructure,
      domainScheme: payload.domainScheme,
      ...(payload.staticTagId != null && { staticTagId: payload.staticTagId }),
    };
    await deps.createPreparedFlex.mutateAsync({
      countryKey: deps.countryKey,
      payload: createPayload,
      wiserId: deps.wiserId,
    });
  }
}
